import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { salidaPublicaApi } from '../api/salidas'
import { comprimirImagen } from '../utils/imagen'
import { useAutoRefresh } from '../hooks/useAutoRefresh'

// ---------------------------------------------------------------------------
// Modales (acciones / fotos)
// ---------------------------------------------------------------------------

function AccionModal({ token, doc, tipo, motivos, onClose, onDone }) {
  const [form, setForm] = useState({ receptor_nombre: '', motivo: '', nueva_fecha: '', notas: '' })
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setEnviando(true)
    try {
      if (tipo === 'entregar') {
        await salidaPublicaApi.entregar(token, doc.id, { receptor_nombre: form.receptor_nombre, notas: form.notas })
      } else if (tipo === 'no_entregar') {
        const motivoFinal = form.motivo === 'Otro' ? (form.motivoOtro || 'Otro') : form.motivo
        await salidaPublicaApi.noEntregar(token, doc.id, { motivo_no_entrega: motivoFinal, notas: form.notas })
      } else {
        await salidaPublicaApi.reagendar(token, doc.id, { nueva_fecha: form.nueva_fecha, notas: form.notas })
      }
      onDone()
    } catch (err) {
      setError(err.response?.data?.message || 'Error. Intentá de nuevo.')
      setEnviando(false)
    }
  }

  return (
    <div style={mdl.overlay}>
      <div style={mdl.sheet}>
        <h2 style={mdl.title}>
          {tipo === 'entregar' ? '✅ Registrar entrega' : tipo === 'no_entregar' ? '❌ No entregado' : '📅 Reagendar'}
        </h2>
        {error && <div style={mdl.error}>{error}</div>}
        <form onSubmit={handleSubmit}>
          {tipo === 'entregar' && (
            <div style={{ marginBottom: 14 }}>
              <label style={mdl.lbl}>¿Quién recibió? *</label>
              <input value={form.receptor_nombre} onChange={e => setForm(f => ({ ...f, receptor_nombre: e.target.value }))} style={mdl.inp} required placeholder="Nombre completo" autoFocus />
            </div>
          )}
          {tipo === 'no_entregar' && (
            <div style={{ marginBottom: 14 }}>
              <label style={mdl.lbl}>Motivo *</label>
              {motivos.length > 0 ? (
                <select value={form.motivo} onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))} style={mdl.inp} required autoFocus>
                  <option value="">— Seleccionar motivo —</option>
                  {motivos.map(m => <option key={m.id} value={m.descripcion}>{m.descripcion}</option>)}
                  <option value="Otro">Otro</option>
                </select>
              ) : (
                <input value={form.motivo} onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))} style={mdl.inp} required placeholder="Ausente, dirección incorrecta..." autoFocus />
              )}
              {form.motivo === 'Otro' && (
                <input value={form.motivoOtro || ''} onChange={e => setForm(f => ({ ...f, motivoOtro: e.target.value }))} style={{ ...mdl.inp, marginTop: 8 }} placeholder="Descripción del motivo" required />
              )}
            </div>
          )}
          {tipo === 'reagendar' && (
            <div style={{ marginBottom: 14 }}>
              <label style={mdl.lbl}>Nueva fecha *</label>
              <input type="date" value={form.nueva_fecha} onChange={e => setForm(f => ({ ...f, nueva_fecha: e.target.value }))} style={mdl.inp} required />
            </div>
          )}
          <div style={{ marginBottom: 20 }}>
            <label style={mdl.lbl}>Observaciones</label>
            <textarea value={form.notas} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} style={{ ...mdl.inp, height: 70, resize: 'vertical' }} placeholder="Opcional" />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button type="button" onClick={onClose} style={mdl.cancelBtn}>Cancelar</button>
            <button type="submit" disabled={enviando} style={{ ...mdl.confirmBtn, background: tipo === 'entregar' ? '#16a34a' : tipo === 'no_entregar' ? '#dc2626' : '#4f46e5' }}>
              {enviando ? 'Guardando...' : 'Confirmar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function FotoModal({ token, doc, onClose }) {
  const inputRef = useRef(null)
  const [fotos, setFotos] = useState([])        // fotos comprimidas elegidas, todavía SIN subir
  const [procesando, setProcesando] = useState(false)
  const [subiendo, setSubiendo] = useState(false)
  const [msg, setMsg] = useState('')

  // Elegir/sacar foto: la comprime y la suma a la previsualización. NO la sube todavía.
  const handleFiles = async (files) => {
    if (!files || files.length === 0) return
    setProcesando(true)
    setMsg('')
    try {
      const nuevas = await Promise.all(Array.from(files).map(f => comprimirImagen(f)))
      setFotos(prev => [...prev, ...nuevas])
    } catch {
      setMsg('Error al procesar la foto.')
    } finally {
      setProcesando(false)
      if (inputRef.current) inputRef.current.value = ''   // permite volver a elegir la misma foto
    }
  }

  const quitar = (idx) => setFotos(prev => prev.filter((_, i) => i !== idx))

  // Botón GUARDAR: recién acá se suben al servidor.
  const guardar = async () => {
    if (fotos.length === 0) return
    setSubiendo(true)
    setMsg('')
    try {
      await salidaPublicaApi.subirImagenes(token, doc.id, fotos)
      setMsg(`${fotos.length} foto${fotos.length > 1 ? 's' : ''} guardada${fotos.length > 1 ? 's' : ''}. ✓`)
      setFotos([])
    } catch {
      setMsg('Error al guardar las fotos. Reintentá.')
    } finally {
      setSubiendo(false)
    }
  }

  const ocupado = procesando || subiendo

  return (
    <div style={mdl.overlay}>
      <div style={mdl.sheet}>
        <h2 style={mdl.title}>📷 Adjuntar fotos</h2>
        {msg && <div style={{ ...mdl.error, background: msg.startsWith('Error') ? '#fee2e2' : '#dcfce7', color: msg.startsWith('Error') ? '#991b1b' : '#166534' }}>{msg}</div>}
        <input ref={inputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />

        {/* Previsualización de las fotos elegidas, antes de guardar */}
        {fotos.length > 0 && (
          <div style={ft.grid}>
            {fotos.map((src, i) => (
              <div key={i} style={ft.thumbWrap}>
                <img src={src} alt={`Foto ${i + 1}`} style={ft.thumb} />
                <button onClick={() => quitar(i)} disabled={subiendo} style={ft.quitar} aria-label="Quitar foto">×</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button onClick={() => { inputRef.current.setAttribute('capture', 'camera'); inputRef.current.click() }} disabled={ocupado} style={{ ...mdl.bigBtn, background: '#0f766e' }}>📷 Tomar foto</button>
          <button onClick={() => { inputRef.current.removeAttribute('capture'); inputRef.current.click() }} disabled={ocupado} style={{ ...mdl.bigBtn, background: '#1e40af' }}>🖼 Elegir de galería</button>

          {/* Botón GUARDAR — visible solo cuando hay fotos pendientes de subir */}
          {fotos.length > 0 && (
            <button onClick={guardar} disabled={ocupado} style={{ ...mdl.bigBtn, background: '#16a34a', boxShadow: '0 4px 0 #15803d' }}>
              {subiendo ? 'Guardando...' : `💾 Guardar ${fotos.length} foto${fotos.length > 1 ? 's' : ''}`}
            </button>
          )}

          <button onClick={onClose} disabled={subiendo} style={{ ...mdl.bigBtn, background: '#f1f5f9', color: '#374151' }}>{fotos.length > 0 ? 'Cancelar' : 'Cerrar'}</button>
        </div>
        {procesando && <p style={{ textAlign: 'center', color: '#64748b', marginTop: 14, fontSize: '0.9rem' }}>Procesando imagen...</p>}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers de datos
// ---------------------------------------------------------------------------

// Reprogramada por el CLIENTE con el pedido en la calle y todavía no
// reconocida por el fletero: sigue en las pendientes, en rojo, hasta que él la
// pasa a completadas (Martin, 7 sep 2026). Antes desaparecía sin explicación.
const esReagendoClientePendiente = (d) => d.estado_entrega === 'reagendado' && !!d.reagendo_cliente_at && !d.reagendo_visto_at
const esPendienteDoc = (d) => !d.estado_entrega || d.estado_entrega === 'pendiente' || esReagendoClientePendiente(d)
// 'YYYY-MM-DD…' → 'DD/MM' sin pasar por Date (evita el corrimiento de zona horaria)
const fmtFechaLarga = (v) => { const [y, m, d] = String(v || '').slice(0, 10).split('-'); return d && m && y ? `${d}/${m}/${y}` : '' }
const fmtDia = (v) => { const [y, m, d] = String(v || '').slice(0, 10).split('-'); return d && m ? `${d}/${m}` : (y || '') }

function getInfo(doc) {
  const ent = doc.entidad || {}
  const esNE = doc.entidad_tipo === 'nota_envio'
  const dest = esNE ? ent.destinatario : ent.cliente
  // T90: dirección de envío de la NV gana sobre la del cliente; remito libre
  // sin NV usa su dirección propia (ent.direccion_envio).
  const envT90 = !esNE ? (ent.nota_venta?.direccion_envio || ent.direccion_envio) : null
  const fuenteDir = !esNE && envT90 && (envT90.calle || envT90.localidad || envT90.codigo_postal)
    ? envT90
    : dest
  return {
    esNE,
    numero: esNE ? ent.numero_ne : ent.numero_presea,
    nombre: fuenteDir?.receptor_nombre || dest?.nombre || '—',
    calle: fuenteDir?.calle || fuenteDir?.domicilio || '',
    localidad: fuenteDir?.localidad || '',
    provincia: fuenteDir?.provincia || '',
    cp: fuenteDir?.codigo_postal || '',
    telefono: fuenteDir?.telefono || dest?.telefono || '',
    wa: dest?.telefono_wa || '',
    // T80 NE trae su propia fecha y horario; T90 hereda fecha del remito (NV → remito)
    fechaEnt: esNE ? ent.fecha_entrega_est : ent.fecha_entrega_est,
    hd: esNE ? ent.hora_desde : null,
    hh: esNE ? ent.hora_hasta : null,
    obs: esNE ? dest?.notas : ent.observaciones_raw,
    // Indicaciones de entrega cargadas en la NV (T90) → viajan al remito.
    notasEntrega: !esNE ? ent.notas_entrega : null,
    articulos: ent.articulos || [],
  }
}

function direccionString(info) {
  return [info.calle, info.localidad, info.provincia, info.cp && `CP ${info.cp}`]
    .filter(Boolean).join(', ')
}

function mapsHref(info) {
  const partes = [info.calle, info.localidad, info.provincia, info.cp].filter(Boolean).join(', ')
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(partes)}`
}

function waHref(numero) {
  const limpio = String(numero).replace(/[^\d]/g, '')
  return `https://wa.me/${limpio.startsWith('54') ? limpio : '54' + limpio}`
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------

export default function FleteroSalidaPage() {
  const { token } = useParams()
  const [salida, setSalida] = useState(null)
  const [motivos, setMotivos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [modal, setModal] = useState(null)
  const [fotoModal, setFotoModal] = useState(null)
  const [completadasOpen, setCompletadasOpen] = useState(false)
  const [refrescando, setRefrescando] = useState(false)

  const cargar = useCallback(async () => {
    try {
      const res = await salidaPublicaApi.obtener(token)
      setSalida(res.data.salida)
      setMotivos(res.data.motivos || [])
    } catch {
      setError('Enlace no válido o salida no encontrada.')
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { cargar() }, [cargar])

  // Refresco solo en background (30s, pausa con la pestaña oculta): si el
  // cliente reprograma o deja una instrucción mientras el fletero está en la
  // calle, la parada se actualiza sola — sin depender del botón refrescar.
  // No refresca con un modal abierto para no mover datos abajo de un form.
  useAutoRefresh(() => { if (!modal && !fotoModal) cargar() }, 30000)

  // 📍 Ubicación de la camioneta (Martin, 31 ago 2026): mientras la hoja de
  // ruta está abierta y la salida en reparto, el navegador manda la posición
  // (cada ~45 s) para el mapa de Distribución. Si el fletero no da permiso o
  // el equipo no soporta geolocalización, no pasa nada: todo sigue igual.
  const ultimoEnvioUbic = useRef(0)
  useEffect(() => {
    if (!salida || salida.estado !== 'despachada' || !navigator.geolocation) return undefined
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const ahora = Date.now()
        if (ahora - ultimoEnvioUbic.current < 45000) return
        ultimoEnvioUbic.current = ahora
        salidaPublicaApi.ubicacion(token, {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          precision: Math.round(pos.coords.accuracy || 0),
        }).catch(() => {})
      },
      () => {},   // sin permiso: silencio — la hoja de ruta no depende de esto
      { enableHighAccuracy: true, maximumAge: 30000 },
    )
    return () => navigator.geolocation.clearWatch(watchId)
  }, [salida?.id, salida?.estado, token])  // eslint-disable-line react-hooks/exhaustive-deps

  const [revirtiendo, setRevirtiendo] = useState(null)

  const handleDone = () => { setModal(null); cargar() }
  const [reconociendo, setReconociendo] = useState(null)
  const handleReagendoVisto = async (doc) => {
    setReconociendo(doc.id)
    try { await salidaPublicaApi.reagendoVisto(token, doc.id); await cargar() }
    catch { /* el próximo cargar lo aclara */ } finally { setReconociendo(null) }
  }
  const refrescar = async () => { setRefrescando(true); await cargar(); setRefrescando(false) }

  const handleRevertir = async (doc) => {
    if (!window.confirm('¿Deshacer esta entrega? La parada vuelve a quedar pendiente para completarla bien.')) return
    setRevirtiendo(doc.id)
    try {
      await salidaPublicaApi.revertirEntrega(token, doc.id)
      await cargar()
    } catch (err) {
      alert(err.response?.data?.message || 'No se pudo deshacer la entrega.')
    } finally {
      setRevirtiendo(null)
    }
  }

  const docs = salida?.documentos || []
  // Override local del orden mientras se persiste el reordenamiento (antes de
  // que el cargar() vuelva con el orden_ruta actualizado del backend). null =
  // usar el orden que vino del show.
  const [ordenLocal, setOrdenLocal] = useState(null) // Map<docId, number> | null
  useEffect(() => { setOrdenLocal(null) }, [salida?.id])

  const { pendientes, hechas } = useMemo(() => {
    const p = [], h = []
    docs.forEach(d => esPendienteDoc(d) ? p.push(d) : h.push(d))
    if (ordenLocal) {
      p.sort((a, b) => (ordenLocal.get(a.id) ?? 9999) - (ordenLocal.get(b.id) ?? 9999))
    }
    return { pendientes: p, hechas: h }
  }, [docs, ordenLocal])

  // ── Botones ▲/▼ para reordenar (touch-friendly, alternativa al drag) ──────
  const [guardandoOrden, setGuardandoOrden] = useState(false)
  const [errorOrden, setErrorOrden] = useState('')

  const mover = async (docId, delta) => {
    const ids = pendientes.map(d => d.id)
    const i = ids.indexOf(docId)
    const j = i + delta
    if (i < 0 || j < 0 || j >= ids.length) return
    ;[ids[i], ids[j]] = [ids[j], ids[i]]

    const m = new Map()
    ids.forEach((id, idx) => m.set(id, idx + 1))
    setOrdenLocal(m)

    setGuardandoOrden(true); setErrorOrden('')
    try {
      await salidaPublicaApi.reordenar(token, ids)
    } catch (err) {
      setErrorOrden(err.response?.data?.message || 'No se pudo guardar el orden.')
      setOrdenLocal(null) // revierte visual
    } finally {
      setGuardandoOrden(false)
    }
  }

  // Ventana de "deshacer" del fletero: las 3 entregas más recientes (por
  // entregado_at desc, id desc para desempatar). Más allá → logística.
  // Debe matchear SalidaPublicaController::REVERTIR_VENTANA = 3.
  const REVERTIR_VENTANA = 3
  const revertiblesIds = useMemo(() => {
    const entregas = hechas
      .filter(d => d.estado_entrega === 'entregado')
      .map(d => ({ id: d.id, t: new Date(d.entregado_at || 0).getTime() }))
      .sort((a, b) => (b.t - a.t) || (b.id - a.id))
      .slice(0, REVERTIR_VENTANA)
      .map(d => d.id)
    return new Set(entregas)
  }, [hechas])
  const total = docs.length
  const hechos = hechas.length
  const pct = total === 0 ? 0 : Math.round((hechos / total) * 100)

  const entregadas   = hechas.filter(d => d.estado_entrega === 'entregado').length
  const noEntregadas = hechas.filter(d => d.estado_entrega === 'no_entregado').length
  const reagendadas  = hechas.filter(d => d.estado_entrega === 'reagendado').length
  const unidPendientes = pendientes.reduce((acc, d) =>
    acc + getInfo(d).articulos.reduce((s, a) => s + Number(a.cantidad || 0), 0), 0)

  if (loading) return (
    <div style={st.full}><p style={{ color: '#94a3b8', fontSize: '1.1rem' }}>Cargando...</p></div>
  )
  if (error) return (
    <div style={st.full}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: '2.5rem', marginBottom: 12 }}>⚠️</p>
        <p style={{ color: '#ef4444', fontWeight: 700, fontSize: '1.1rem' }}>{error}</p>
      </div>
    </div>
  )

  return (
    <div style={st.wrap}>
      {modal && <AccionModal token={token} doc={modal.doc} tipo={modal.tipo} motivos={motivos} onClose={() => setModal(null)} onDone={handleDone} />}
      {fotoModal && <FotoModal token={token} doc={fotoModal} onClose={() => setFotoModal(null)} />}

      {/* HEADER STICKY con progreso */}
      <div style={st.header}>
        <div style={st.headerTop}>
          <div style={{ minWidth: 0 }}>
            {/* La fecha llega como ISO ('2026-09-07T03:00:00Z'); al fletero se le muestra DD/MM/AAAA */}
            <p style={st.headerLbl}>Distribución · {fmtFechaLarga(salida?.fecha)}</p>
            <h1 style={st.headerName}>{salida?.fletero?.nombre}</h1>
          </div>
          <div style={st.headerRight}>
            <button onClick={refrescar} disabled={refrescando} style={st.refreshBtn} title="Actualizar" aria-label="Actualizar">
              <span style={{ display: 'inline-block', transition: 'transform .6s', transform: refrescando ? 'rotate(360deg)' : 'none' }}>↻</span>
            </button>
            <div style={st.headerCount}>
              <strong style={st.headerCountNum}>{hechos}</strong>
              <span style={st.headerCountTot}>/ {total}</span>
            </div>
          </div>
        </div>
        <div style={st.progressWrap}>
          <div style={{ ...st.progressFill, width: `${pct}%` }} />
        </div>
        {hechos > 0 && (
          <div style={st.breakdown}>
            {entregadas > 0 && <span style={st.bdItem}>✓ {entregadas} entregadas</span>}
            {noEntregadas > 0 && <span style={st.bdItem}>✕ {noEntregadas} no entregadas</span>}
            {reagendadas > 0 && <span style={st.bdItem}>↻ {reagendadas} reagendadas</span>}
          </div>
        )}
      </div>

      {/* PENDIENTES */}
      {pendientes.length === 0 ? (
        <div style={st.finalizado}>
          <p style={{ fontSize: '3rem', marginBottom: 8 }}>🎉</p>
          <p style={{ fontWeight: 800, color: '#166534', fontSize: '1.1rem' }}>¡Todas las entregas hechas!</p>
        </div>
      ) : (
        <div style={st.lista}>
          <h2 style={st.seccionTitulo}>
            PRÓXIMAS ENTREGAS · {pendientes.length} parada{pendientes.length !== 1 ? 's' : ''}
            {unidPendientes > 0 && ` · ${unidPendientes} unid.`}
          </h2>
          {(guardandoOrden || errorOrden) && (
            <p style={st.dragHint}>
              {errorOrden ? `⚠ ${errorOrden}` : 'Guardando orden…'}
            </p>
          )}
          {pendientes.map((doc, idx) => {
            const info = getInfo(doc)
            const dirCompleta = direccionString(info)
            const horario = info.hd || info.hh ? `${info.hd || '—'} a ${info.hh || '—'}` : null
            const totalUnid = info.articulos.reduce((acc, a) => acc + Number(a.cantidad || 0), 0)
            const esProxima = idx === 0
            const nLinks = [dirCompleta, info.telefono, info.wa].filter(Boolean).length
            const esPrimero = idx === 0
            const esUltimo = idx === pendientes.length - 1
            return (
              <div key={doc.id} style={{ ...st.card, ...(esProxima ? st.cardProxima : {}) }}>
                {esProxima && <div style={st.proximaTag}>👉 PRÓXIMA</div>}

                {/* Nombre y nº */}
                <div style={st.cardHead}>
                  <span style={{ ...st.paradaNum, ...(esProxima ? st.paradaNumProx : {}) }}>{idx + 1}</span>
                  <div style={st.moverCol}>
                    <button onClick={() => mover(doc.id, -1)} disabled={esPrimero || guardandoOrden} style={st.btnMover} title="Subir parada" aria-label="Subir parada">▲</button>
                    <button onClick={() => mover(doc.id, +1)} disabled={esUltimo || guardandoOrden} style={st.btnMover} title="Bajar parada" aria-label="Bajar parada">▼</button>
                  </div>
                  <h3 style={st.persona}>{info.nombre}</h3>
                  <code style={st.numChip}>{info.numero}</code>
                </div>

                {/* Dirección */}
                {dirCompleta && <p style={st.direccion}>📍 {dirCompleta}</p>}

                {/* Horario y artículos */}
                <div style={st.metaRow}>
                  {horario && <span style={st.metaTag}>🕐 {horario}</span>}
                  {totalUnid > 0 && <span style={st.metaTag}>📦 {totalUnid} unid.</span>}
                </div>

                {/* Artículos como chips */}
                {info.articulos.length > 0 && (
                  <div style={st.artsRow}>
                    {info.articulos.map(a => (
                      <span key={a.id} style={st.artChip} title={a.articulo?.descripcion}>
                        <code style={st.artCod}>{a.articulo?.codigo_alfa}</code>
                        <span>× {a.cantidad}</span>
                      </span>
                    ))}
                  </div>
                )}

                {/* PAGO CONTRA ENTREGA (condición 5/31): esta parada SE COBRA.
                    Banda fuerte arriba de todo lo demás — es plata. */}
                {doc.pago_contra_entrega && (
                  <p style={st.contraEntrega}>
                    💵 PAGO CONTRA ENTREGA — COBRAR ANTES DE ENTREGAR
                    {/* Total de la factura (solo remitos; null si el detalle fiscal no llegó) */}
                    {doc.importe_contra_entrega != null && (
                      <span style={st.contraEntregaImporte}>
                        COBRAR $ {Number(doc.importe_contra_entrega).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    )}
                  </p>
                )}

                {/* Indicaciones que cargó el vendedor en la NV (T90) */}
                {info.notasEntrega && <p style={st.notasEntrega}>📋 {info.notasEntrega}</p>}

                {/* Instrucciones que dejó EL CLIENTE desde su portal de seguimiento */}
                {doc.instrucciones_cli && <p style={st.instCliente}>🛎️ Cliente: {doc.instrucciones_cli}</p>}

                {/* Observaciones */}
                {info.obs && <p style={st.obs}>💬 {info.obs}</p>}

                {/* Acciones secundarias: maps + tel + whatsapp */}
                {nLinks > 0 && (
                  <div style={{ ...st.linksRow, gridTemplateColumns: `repeat(${nLinks}, 1fr)` }}>
                    {dirCompleta && (
                      <a href={mapsHref(info)} target="_blank" rel="noreferrer" style={st.btnLink}>
                        🗺 Cómo llegar
                      </a>
                    )}
                    {info.telefono && (
                      <a href={`tel:${info.telefono}`} style={st.btnLink}>
                        📞 Llamar
                      </a>
                    )}
                    {info.wa && (
                      <a href={waHref(info.wa)} target="_blank" rel="noreferrer" style={{ ...st.btnLink, ...st.btnWa }}>
                        💬 WhatsApp
                      </a>
                    )}
                  </div>
                )}

                {esReagendoClientePendiente(doc) ? (
                  <>
                    {/* El CLIENTE la reprogramó con el pedido en la calle: no se
                        entrega. El fletero la reconoce y recién ahí pasa a completadas. */}
                    <p style={st.bandaReagendo}>📅 EL CLIENTE REPROGRAMÓ PARA EL {fmtDia(doc.nueva_fecha)} — NO ENTREGAR</p>
                    <button onClick={() => handleReagendoVisto(doc)} disabled={reconociendo === doc.id} style={st.btnNoEntXL}>
                      {reconociendo === doc.id ? '...' : '❌ NO ENTREGAR · CLIENTE REAGENDÓ'}
                    </button>
                  </>
                ) : (
                  <>
                    {/* Acción primaria: ENTREGADO grande */}
                    <button onClick={() => setModal({ doc, tipo: 'entregar' })} style={st.btnEntregadoXL}>
                      ✅ MARCAR COMO ENTREGADO
                    </button>

                    {/* Acciones secundarias destructivas */}
                    <div style={st.btnSecRow}>
                      <button onClick={() => setModal({ doc, tipo: 'no_entregar' })} style={st.btnNoEnt}>❌ No entregado</button>
                      <button onClick={() => setModal({ doc, tipo: 'reagendar' })} style={st.btnReag}>📅 Reagendar</button>
                      <button onClick={() => setFotoModal(doc)} style={st.btnFoto}>📷</button>
                    </div>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* COMPLETADAS — colapsadas por defecto */}
      {hechas.length > 0 && (
        <div style={st.completadas}>
          <button onClick={() => setCompletadasOpen(s => !s)} style={st.completadasToggle}>
            <span>{completadasOpen ? '▼' : '▶'} Completadas ({hechas.length})</span>
          </button>
          {completadasOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
              {hechas.map(doc => {
                const info = getInfo(doc)
                const cfg = doc.estado_entrega === 'entregado'
                  ? { icon: '✓', bg: '#16a34a', label: 'Entregado' }
                  : doc.estado_entrega === 'no_entregado'
                  ? { icon: '✕', bg: '#dc2626', label: 'No entregado' }
                  : { icon: '↻', bg: '#4f46e5', label: doc.reagendo_cliente_at ? `Reagendado por el cliente · ${fmtDia(doc.nueva_fecha)}` : `Reagendado${doc.nueva_fecha ? ' · '+fmtDia(doc.nueva_fecha) : ''}` }
                const puedeRevertir = revertiblesIds.has(doc.id)
                return (
                  <div key={doc.id} style={st.cardMini}>
                    <span style={{ ...st.miniBadge, background: cfg.bg }}>{cfg.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={st.miniNombre}>{info.nombre}</p>
                      <p style={st.miniSub}>{cfg.label}{doc.receptor_nombre && ` · ${doc.receptor_nombre}`}{doc.motivo_no_entrega && ` · ${doc.motivo_no_entrega}`}</p>
                    </div>
                    {/* Sug #60: la foto del remito firmado también DESPUÉS de
                        entregar — el cliente firma y el fletero se acuerda al
                        subir al auto. Mismo modal que en las próximas. */}
                    <button onClick={() => setFotoModal(doc)} style={st.btnFoto} title="Adjuntar foto (remito firmado)">📷</button>
                    {puedeRevertir && (
                      <button
                        onClick={() => handleRevertir(doc)}
                        disabled={revirtiendo === doc.id}
                        style={st.btnDeshacer}
                        title="Deshacer (solo se pueden deshacer las últimas 3 entregas)"
                      >
                        {revirtiendo === doc.id ? '...' : '↩ Deshacer'}
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Estilos
// ---------------------------------------------------------------------------

const st = {
  full: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', padding: 24 },
  wrap: { minHeight: '100vh', background: '#f8fafc', maxWidth: 480, margin: '0 auto', paddingBottom: 24 },

  // Header sticky
  header: { position: 'sticky', top: 0, zIndex: 5, background: '#1e40af', padding: '14px 16px', color: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  headerTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  headerLbl: { fontSize: '0.7rem', opacity: 0.85, margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' },
  headerName: { margin: '2px 0 0', fontSize: '1.15rem', fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  headerRight: { display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  refreshBtn: { width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: '#fff', fontSize: '1.2rem', fontWeight: 800, cursor: 'pointer', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  headerCount: { textAlign: 'right' },
  headerCountNum: { fontSize: '1.6rem', fontWeight: 900, lineHeight: 1 },
  headerCountTot: { fontSize: '0.95rem', opacity: 0.7, marginLeft: 2 },
  progressWrap: { marginTop: 10, height: 6, background: 'rgba(255,255,255,0.2)', borderRadius: 3, overflow: 'hidden' },
  progressFill: { height: '100%', background: '#22c55e', transition: 'width 0.3s ease' },
  breakdown: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 },
  bdItem: { fontSize: '0.72rem', fontWeight: 700, background: 'rgba(255,255,255,0.15)', padding: '2px 8px', borderRadius: 8 },

  // Listas
  lista: { padding: '12px 12px 0' },
  seccionTitulo: { fontSize: '0.72rem', fontWeight: 800, color: '#64748b', letterSpacing: '0.06em', margin: '4px 4px 10px' },

  // Cards
  card: { background: '#fff', borderRadius: 14, padding: 14, marginBottom: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' },
  cardProxima: { border: '2px solid #1e40af', boxShadow: '0 4px 14px rgba(30,64,175,0.15)' },
  proximaTag: { background: '#1e40af', color: 'white', fontSize: '0.7rem', fontWeight: 800, padding: '3px 10px', borderRadius: 12, alignSelf: 'flex-start', marginBottom: 8, display: 'inline-block', letterSpacing: '0.05em' },

  cardHead: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 },
  paradaNum: { width: 26, height: 26, borderRadius: '50%', background: '#e2e8f0', color: '#475569', fontSize: '0.85rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  paradaNumProx: { background: '#1e40af', color: '#fff' },
  moverCol: { display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 },
  btnMover: { width: 32, height: 22, border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', color: '#475569', fontSize: '0.7rem', fontWeight: 700, padding: 0, cursor: 'pointer', lineHeight: 1 },
  dragHint: { fontSize: '0.78rem', color: '#64748b', margin: '0 0 10px', fontStyle: 'italic' },
  persona: { margin: 0, fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2, flex: 1, minWidth: 0 },
  numChip: { fontSize: '0.7rem', fontWeight: 700, color: '#475569', background: '#f1f5f9', padding: '2px 8px', borderRadius: 6, flexShrink: 0 },

  direccion: { margin: '6px 0 4px', fontSize: '0.95rem', color: '#334155', lineHeight: 1.35 },

  metaRow: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 },
  metaTag: { fontSize: '0.78rem', fontWeight: 700, color: '#1e40af', background: '#eff6ff', padding: '3px 10px', borderRadius: 10 },

  artsRow: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 },
  artChip: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', background: '#f1f5f9', color: '#475569', padding: '3px 8px', borderRadius: 8, fontWeight: 600 },
  artCod: { fontFamily: 'monospace', fontWeight: 800, color: '#0f172a' },

  obs: { margin: '10px 0 0', fontSize: '0.85rem', color: '#92400e', background: '#fefce8', border: '1px solid #fde68a', padding: '8px 10px', borderRadius: 8, lineHeight: 1.4 },
  contraEntrega: { margin: '10px 0 0', fontSize: '0.85rem', fontWeight: 800, letterSpacing: '0.03em', color: '#fff', background: '#111', border: '2px solid #000', padding: '10px 12px', borderRadius: 8, textAlign: 'center', lineHeight: 1.3 },
  contraEntregaImporte: { display: 'block', marginTop: 4, fontSize: '1.35rem', fontWeight: 900, letterSpacing: '0.02em', whiteSpace: 'nowrap' },
  notasEntrega: { margin: '10px 0 0', fontSize: '0.85rem', color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '8px 10px', borderRadius: 8, lineHeight: 1.4 },
  instCliente: { margin: '10px 0 0', fontSize: '0.85rem', color: '#0f766e', background: '#f0fdfa', border: '1px solid #99f6e4', padding: '8px 10px', borderRadius: 8, lineHeight: 1.4, fontWeight: 600 },

  linksRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 },
  btnLink: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 8px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 10, color: '#1e293b', fontWeight: 700, fontSize: '0.85rem', textDecoration: 'none', textAlign: 'center' },
  btnWa: { background: '#dcfce7', border: '1px solid #86efac', color: '#166534' },

  bandaReagendo: { margin: '12px 0 0', padding: '10px 12px', background: '#dc2626', color: '#fff', borderRadius: 10, fontWeight: 800, fontSize: '0.85rem', letterSpacing: '0.02em', lineHeight: 1.3 },
  btnNoEntXL: { width: '100%', padding: '16px', marginTop: 10, background: '#fff', color: '#dc2626', border: '2px solid #dc2626', borderRadius: 12, fontWeight: 800, fontSize: '1rem', cursor: 'pointer', boxShadow: '0 4px 0 #fca5a5', letterSpacing: '0.03em' },
  btnEntregadoXL: { width: '100%', padding: '16px', marginTop: 12, background: '#16a34a', color: 'white', border: 'none', borderRadius: 12, fontWeight: 800, fontSize: '1rem', cursor: 'pointer', boxShadow: '0 4px 0 #15803d', letterSpacing: '0.03em' },

  btnSecRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 50px', gap: 6, marginTop: 8 },
  btnNoEnt: { padding: '10px 8px', background: 'white', border: '1.5px solid #fca5a5', color: '#dc2626', borderRadius: 10, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' },
  btnReag: { padding: '10px 8px', background: 'white', border: '1.5px solid #c7d2fe', color: '#4338ca', borderRadius: 10, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' },
  btnFoto: { padding: '10px 8px', background: '#f1f5f9', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: '1rem', cursor: 'pointer' },

  // Completadas
  completadas: { padding: '8px 12px 0' },
  completadasToggle: { width: '100%', background: 'transparent', border: 'none', padding: '10px 4px', textAlign: 'left', fontSize: '0.8rem', fontWeight: 800, color: '#64748b', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.06em' },
  cardMini: { display: 'flex', alignItems: 'center', gap: 10, background: '#fff', padding: '10px 12px', borderRadius: 10, border: '1px solid #f1f5f9' },
  miniBadge: { width: 28, height: 28, borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 800, flexShrink: 0 },
  miniNombre: { margin: 0, fontSize: '0.9rem', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  miniSub: { margin: '2px 0 0', fontSize: '0.72rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  btnDeshacer: { flexShrink: 0, padding: '8px 12px', background: 'white', border: '1.5px solid #cbd5e1', color: '#475569', borderRadius: 10, fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' },

  finalizado: { padding: '40px 16px', textAlign: 'center' },
}

const mdl = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', zIndex: 100 },
  sheet: { background: '#fff', borderRadius: '20px 20px 0 0', padding: '24px 20px', width: '100%', boxSizing: 'border-box', maxWidth: 480, margin: '0 auto' },
  title: { fontSize: '1.1rem', fontWeight: 700, marginBottom: 18, color: '#1e293b' },
  error: { background: '#fee2e2', color: '#991b1b', padding: '10px', borderRadius: 8, marginBottom: 14, fontSize: '0.9rem' },
  lbl: { display: 'block', fontSize: '0.9rem', fontWeight: 600, color: '#475569', marginBottom: 6 },
  inp: { width: '100%', padding: '12px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: '1rem', boxSizing: 'border-box' },
  cancelBtn: { flex: 1, padding: '14px', borderRadius: 12, background: '#f1f5f9', border: 'none', fontWeight: 700, fontSize: '1rem', cursor: 'pointer' },
  confirmBtn: { flex: 2, padding: '14px', borderRadius: 12, color: '#fff', border: 'none', fontWeight: 700, fontSize: '1rem', cursor: 'pointer' },
  bigBtn: { width: '100%', padding: '14px', borderRadius: 12, color: '#fff', border: 'none', fontWeight: 700, fontSize: '1rem', cursor: 'pointer' },
}

// Previsualización de fotos en el FotoModal
const ft = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 },
  thumbWrap: { position: 'relative', aspectRatio: '1', borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' },
  thumb: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  quitar: { position: 'absolute', top: 4, right: 4, width: 24, height: 24, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', fontSize: '1rem', fontWeight: 800, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
}
